import React, { useContext, useEffect, useRef } from "react"
import { View, Text, TouchableOpacity, Animated } from "react-native"
import Loader from "./loader"
import { textStyles } from "../style"
import { ThemeContext } from "../utility/context/ThemeContext"
import { Ionicons } from "@expo/vector-icons"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import { useRoute } from "@react-navigation/native"
import { MAX_CONTENT_WIDTH } from "../config"
import Haptics from "./haptics"
import { log } from "./pwa"

type Props = {
  children: (props: {
      setStepHandler: (handler: () => Promise<boolean> | void) => void
      setNextEnabled: (enabled: boolean) => void
      loading: boolean
      setLoading: (loading: boolean) => void
    }) => React.ReactNode
  stepIndex: number
  totalSteps: number
  title: string
  onNext: () => void
  onBack: () => void
  nextEnabled?: boolean
}

const OnboardingLayout = ({
  children,
  stepIndex,
  totalSteps,
  title,
  onNext,
  onBack,
}: Props): React.ReactElement<Props> => {
  const theme = useContext(ThemeContext).theme
  const { textMain, borderColor, primary, bgMain } = theme.colors
  const route = useRoute()
  const back = route.params?.back || false

  const progressAnim = useRef(
    new Animated.Value(
      back
        ? ((stepIndex + 1) / (totalSteps - 1)) * 100
        : ((stepIndex - 1) / (totalSteps - 1)) * 100
    )
  ).current

  const stepHandlerRef = useRef<() => Promise<boolean> | void>(undefined)
  const [nextEnabled, setNextEnabled] = React.useState(true)
  const [loading, setLoading] = React.useState(false)

  const insets = useSafeAreaInsets()

  const setStepHandler = (handler: () => Promise<boolean> | void) => {
    stepHandlerRef.current = handler
  }

  const handleNext = async () => {
    if (!stepHandlerRef.current) {
      onNext()
      return
    }

    try {
      const result = await stepHandlerRef.current()
      if (result !== false) {
        onNext()
      }
    } catch (err) {
      log.error(err, { tags: { message: 'error in onboarding step handler' } })
    }
  }

  useEffect(() => {
    const progress = (stepIndex / (totalSteps - 1)) * 100

    Animated.timing(progressAnim, {
      toValue: progress,
      duration: 300,
      useNativeDriver: false,
    }).start()
  }, [stepIndex])

  return (
    <View style={{flex: 1, justifyContent: "center", alignItems: "center", paddingVertical: 16, backgroundColor: bgMain, paddingHorizontal: 16}}>
    <View style={{flex: 1, width: "100%", gap: 10, maxWidth: MAX_CONTENT_WIDTH}}>
      {/* Progress Bar */}
      <View style={{ width: "100%", borderRadius: 10, borderColor: borderColor, borderWidth: 1, overflow: "hidden", flexDirection: "row" }}>
        <Animated.View
          style={{
            backgroundColor: primary,
            width: progressAnim.interpolate({
              inputRange: [0, 100],
              outputRange: ["0%", "100%"],
            }),
            height: 10
          }}
        />
      </View>
      
      <Text style={[textStyles.h1, {color: textMain}]}>{title}</Text>

      <View style={{ flex: 1, width: "100%" }}>
        {children({ setStepHandler, setNextEnabled, loading, setLoading })}
      </View>

      {/* Navigation Buttons */}
      <View
        style={{
          flexDirection: "row",
          justifyContent: "space-between",
          height: 50,
          alignItems: "center",
          marginBottom: insets.bottom
        }}
      >
        {stepIndex > 0 ? (
          <OnboardingButton
            title="Previous"
            onPress={onBack}
          />
        ) : <View />}
        <OnboardingButton
          title={stepIndex === totalSteps - 1 ? "Complete" : "Next"}
          onPress={handleNext}
          disabled={!nextEnabled}
          loading={loading}
        />
      </View>
    </View>
    </View>
  )
}

export default OnboardingLayout

type OnboardingButtonProps = {
  title: "Next" | "Previous" | "Complete"
  onPress: () => void
  disabled?: boolean
  loading?: boolean
}

const OnboardingButton = ({title, onPress, disabled, loading}: OnboardingButtonProps) => {
  const theme = useContext(ThemeContext).theme
  const { primary, white, disabled: disabledColor, textAlt } = theme.colors

  if (title === "Previous") {
    return (
      <TouchableOpacity
        onPress={() => {
          Haptics.impactMedium()
          onPress()
        }}
        disabled={disabled || loading}
      >
        <View style={{flexDirection: "row", gap: 5, alignItems: "center"}}>
          <Ionicons name="arrow-back" size={16} color={textAlt} />
          <Text style={[textStyles.h4, {color: textAlt}]}>{title}</Text>
        </View>
      </TouchableOpacity>
    )
  }

  return (
    <TouchableOpacity
      onPress={() => {
        Haptics.impactSoft()
        onPress()
      }}
      disabled={disabled || loading}
    >
      <View style={{flexDirection: "row", gap: 5, borderRadius: 8, paddingVertical: 5, paddingHorizontal: 10, backgroundColor: disabled ? disabledColor : primary, alignItems: "center"}}>
        <Text style={[textStyles.h4, {color: white}]}>{title}</Text>
        {title === "Next" && !loading ? <Ionicons name="arrow-forward" size={16} color={white} /> : null}
        {loading && <Loader size="small" style={{marginLeft: 5}} />}
      </View>
    </TouchableOpacity>
  )
}